import { Controller } from "@hotwired/stimulus"

// P2-1: single Stripe card-collection controller used by ALL three payment
// surfaces (public booking new-card, portal booking new-card, portal Add-Card).
// Replaces the prior payment_controller.js + booking_payment_controller.js
// which had drifted from each other and used the deprecated cardNumber/
// cardExpiry/cardCvc Elements with no inline 3DS handling.
//
// Two modes via data-stripe-card-mode-value:
//
//   "setup_intent" — Portal "Add a card" flow. Fetches a SetupIntent
//   client_secret from the server (the endpoint added in P0-10), mounts
//   PaymentElement scoped to that secret, and confirms via
//   stripe.confirmSetup() so the off-session mandate is recorded AND any
//   3DS challenge is handled inline. On success, posts the setup_intent_id
//   to the form action — PaymentMethods::AttachService retrieves the
//   SetupIntent and finalizes the local row.
//
//   "payment_method" — Booking flow. Creates an Elements instance in
//   mode: 'setup' (no server-side SetupIntent needed), mounts PaymentElement,
//   then on submit calls stripe.createPaymentMethod() to produce a pm_*.
//   The server's authorize-then-capture path takes it from there. 3DS
//   challenges that surface at capture time fall back to the P0-9
//   friendly-error message ("Use a different card") — the booking flow
//   doesn't yet support inline 3DS because the PaymentIntent is created
//   server-side after capture. That refactor is a future task.
//
// Configuration via data attributes:
//   data-stripe-card-mode-value          → "setup_intent" or "payment_method"
//   data-stripe-card-stripe-account-value → "acct_xxx" (Connect account)
//   data-stripe-card-setup-intent-url-value → URL to POST for client_secret
//                                              (setup_intent mode only)
//   data-stripe-card-form-action-value   → URL to POST the form to
//                                              (setup_intent mode only;
//                                              payment_method mode posts to
//                                              the natural form action)
//   data-stripe-card-return-url-value    → URL to redirect back to after
//                                              an off-site redirect (3DS
//                                              with payment methods that
//                                              redirect — rare for cards).
//
// Targets:
//   element    → mount point for PaymentElement (iframe paints here behind
//                the loading overlay, clipped by the container's height lock)
//   container  → relative wrapper with the height lock + overflow-hidden
//   loading    → spinner overlay shown until Stripe iframe height is stable
//   errors     → element for surfacing user-facing errors
//   submit     → submit button (for disabled-state during processing)
//   submitText → optional <span> inside submit button (for "Processing...")
//   paymentMethodId → hidden input that receives the pm_* (payment_method mode)
//   setupIntentId   → hidden input that receives the seti_* (setup_intent mode)
//
// Note: card-only payments collect billing details server-side or via
// PaymentElement's built-in fields. We use redirect: 'if_required' so
// most card payments (which don't redirect) complete in place.
export default class extends Controller {
  static targets = [
    "element",
    "container",
    "loading",
    "errors",
    "submit",
    "submitText",
    "paymentMethodId",
    "setupIntentId"
  ]

  static values = {
    mode: { type: String, default: "payment_method" },
    stripeAccount: String,
    setupIntentUrl: String,
    formAction: String,
    returnUrl: String
  }

  // This controller uses Stripe Elements at its stock defaults inside
  // the iframe — no appearance overrides, no layout overrides, no
  // paymentMethodOrder. Stripe controls every visual detail inside
  // the iframe.
  //
  // Two host-level customizations remain:
  //   1) payment_method_types: ["card"] on the booking-flow elements
  //      instance — a business rule (no ACH for bookings).
  //   2) loader: "never" — we cover the element with our own loading
  //      overlay (see the shared partial) until Stripe is fully
  //      painted, so the user doesn't see Stripe's skeleton placeholders
  //      flicker before the real form, and doesn't see the secondary
  //      resize when Link's "save my information" section loads.

  connect() {
    this.destroyed = false
    this.timers = new Set()
    this.waitForStripe()
  }

  disconnect() {
    // F-HIGH-2: cancel every in-flight async so a Turbo navigation
    // mid-load doesn't leak timers, polling loops, or the SetupIntent
    // fetch (which would otherwise create an orphan SetupIntent at
    // Stripe). All scheduleTimer callbacks check this.destroyed and
    // bail; the AbortController cancels the fetch.
    this.destroyed = true
    this.timers?.forEach((id) => clearTimeout(id))
    this.timers?.clear()
    this.abortController?.abort()
    this.paymentElement?.unmount?.()
    this.paymentElement = null
  }

  // Wraps setTimeout so cancellation is bookkeeping-free. Returns the
  // numeric timer id (rarely needed) or null when called after disconnect.
  scheduleTimer(callback, delay) {
    if (this.destroyed) return null
    const id = setTimeout(() => {
      this.timers?.delete(id)
      if (this.destroyed) return
      callback()
    }, delay)
    this.timers?.add(id)
    return id
  }

  // F-HIGH-1: polls for window.Stripe up to STRIPE_LOAD_TIMEOUT_MS,
  // then fails with an actionable error rather than polling forever.
  // The deadline is captured on the first invocation so recursive
  // calls don't reset the clock.
  waitForStripe(deadline = null) {
    if (this.destroyed) return
    const STRIPE_LOAD_TIMEOUT_MS = 8000
    deadline ??= Date.now() + STRIPE_LOAD_TIMEOUT_MS

    if (typeof Stripe !== "undefined") {
      this.initialize()
      return
    }

    if (Date.now() >= deadline) {
      this.showError("Couldn't reach the payment service. Check your connection or any ad blockers blocking js.stripe.com, then reload the page.")
      this.dismissLoading()
      return
    }

    this.scheduleTimer(() => this.waitForStripe(deadline), 100)
  }

  async initialize() {
    const publishableKey = document.querySelector('meta[name="stripe-publishable-key"]')?.content
    if (!publishableKey) {
      this.showError("Payment setup unavailable. Please reload the page.")
      this.dismissLoading()
      return
    }

    const stripeOpts = {}
    if (this.hasStripeAccountValue && this.stripeAccountValue) {
      stripeOpts.stripeAccount = this.stripeAccountValue
    }
    this.stripe = Stripe(publishableKey, stripeOpts)

    if (this.modeValue === "setup_intent") {
      await this.initializeForSetupIntent()
    } else {
      this.initializeForPaymentMethod()
    }
  }

  async initializeForSetupIntent() {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    // F-HIGH-2: track the AbortController so disconnect() can cancel an
    // in-flight SetupIntent fetch (otherwise a Turbo nav-away creates
    // an orphan SetupIntent at Stripe).
    this.abortController = new AbortController()
    let resp
    try {
      resp = await fetch(this.setupIntentUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          Accept: "application/json"
        },
        body: "{}",
        signal: this.abortController.signal
      })
    } catch (e) {
      if (e.name === "AbortError") return
      this.showError("Card form didn't load. Reload the page and try again.")
      this.dismissLoading()
      return
    }
    if (this.destroyed) return
    if (!resp.ok) {
      this.showError("Card form didn't load. Reload the page and try again.")
      this.dismissLoading()
      return
    }
    const data = await resp.json()
    if (this.destroyed) return
    this.clientSecret = data.client_secret

    this.elements = this.stripe.elements({
      clientSecret: this.clientSecret,
      loader: "never"
    })
    this.paymentElement = this.elements.create("payment", {
      // Link is Stripe's cross-merchant saved-card autofill. The
      // returning-client conversion bump it provides doesn't apply
      // to this product (repeat clients pay from saved cards in the
      // portal, not via Link), and disabling it keeps the iframe
      // from doing the async "save my information" row that
      // resized the form mid-load and dragged the loading state
      // out by a second.
      wallets: { link: "never" }
    })
    this.paymentElement.mount(this.elementTarget)
    this.paymentElement.on("ready", () => this.handleReady())
    this.paymentElement.on("change", (e) => this.handleChange(e))
  }

  initializeForPaymentMethod() {
    this.elements = this.stripe.elements({
      mode: "setup",
      currency: "usd",
      paymentMethodCreation: "manual",
      loader: "never",
      // V1: cards only on the booking flow. Restrict at the Elements
      // layer so Stripe doesn't surface Bank/Cash App Pay tabs the
      // server-side capture path doesn't handle gracefully.
      payment_method_types: ["card"]
    })
    this.paymentElement = this.elements.create("payment", {
      wallets: { link: "never" }
    })
    this.paymentElement.mount(this.elementTarget)
    this.paymentElement.on("ready", () => this.handleReady())
    this.paymentElement.on("change", (e) => this.handleChange(e))
  }

  // With Link disabled (see wallets: { link: "never" } above), the
  // iframe doesn't have the async "save my information" row that
  // used to land 300-800ms after `ready` and resize the form. The
  // only thing left to wait on is Stripe's internal cross-origin
  // paint of the card fields — we can't detect that directly, so
  // we poll iframe.offsetHeight for size stability and add a small
  // paint buffer afterwards. While all of this happens, the
  // container is height-locked at 220px with overflow hidden, so
  // intermediate states are invisible.
  handleReady() {
    if (this.readyHandled) return
    this.readyHandled = true

    this.waitForStableHeight().then(() => {
      this.scheduleTimer(() => this.revealForm(), 100)
    })
  }

  waitForStableHeight() {
    return new Promise((resolve) => {
      const POLL_MS = 60
      const MIN_WAIT_MS = 300           // Don't reveal before this, even if stable
      const STABLE_THRESHOLD_MS = 200   // Require height unchanged this long
      const MAX_WAIT_MS = 2000          // Hard ceiling, reveal regardless

      const startedAt = Date.now()
      let lastHeight = -1
      let lastChangeAt = startedAt

      const tick = () => {
        if (this.destroyed) return resolve()
        const iframe = this.elementTarget?.querySelector("iframe")
        const h = iframe?.offsetHeight || 0
        const now = Date.now()
        const elapsed = now - startedAt

        if (h !== lastHeight) {
          lastHeight = h
          lastChangeAt = now
        }

        const stableFor = now - lastChangeAt
        const stableLongEnough = h > 0 && stableFor >= STABLE_THRESHOLD_MS
        const waitedLongEnough = elapsed >= MIN_WAIT_MS

        if (waitedLongEnough && stableLongEnough) return resolve()
        if (elapsed >= MAX_WAIT_MS) return resolve()
        this.scheduleTimer(tick, POLL_MS)
      }

      tick()
    })
  }

  // Snap-swap in a single frame: release the container's height
  // lock + overflow clip, and hide the spinner. The iframe is
  // already painted at its final size, so the user sees one clean
  // transition from spinner card → form card. Safe to call more
  // than once.
  revealForm() {
    if (this.formRevealed) return
    this.formRevealed = true

    if (this.hasContainerTarget) {
      this.containerTarget.classList.remove("h-[200px]", "overflow-hidden")
    }
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
      this.submitTarget.setAttribute("aria-busy", "false")
    }
  }

  // Used by init-failure paths so the spinner doesn't hang forever
  // when we never get to `ready` (missing publishable key, SetupIntent
  // fetch failure, etc.). Releases the container's height lock too so
  // the user doesn't see an empty 220px card hovering above the
  // inline error message.
  dismissLoading() {
    if (this.hasContainerTarget) {
      this.containerTarget.classList.remove("h-[200px]", "overflow-hidden")
    }
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
  }

  handleChange(event) {
    if (event.error) {
      this.showError(event.error.message)
    } else {
      this.clearError()
    }
  }

  showError(message) {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.textContent = message
    this.errorsTarget.classList.remove("hidden")
  }

  clearError() {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.textContent = ""
    this.errorsTarget.classList.add("hidden")
  }

  setSubmitBusy(busy) {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = busy
      // F-HIGH-8: aria-busy lets assistive tech announce that the form
      // is processing; native `disabled` alone doesn't surface the
      // "wait, work is happening" state to screen readers.
      this.submitTarget.setAttribute("aria-busy", busy ? "true" : "false")
    }
    if (this.hasSubmitTextTarget && busy) {
      this.originalSubmitText ||= this.submitTextTarget.textContent
      this.submitTextTarget.textContent = "Processing..."
    } else if (this.hasSubmitTextTarget && !busy && this.originalSubmitText) {
      this.submitTextTarget.textContent = this.originalSubmitText
    }
    // Pair the submitting flag with the busy state — every error path
    // calls setSubmitBusy(false) before returning, so a retry after an
    // error is allowed to proceed. Success paths navigate via .submit()
    // and leave submitting set, which is correct (the page is leaving).
    if (!busy) {
      this.submitting = false
    }
  }

  async handleSubmit(event) {
    event.preventDefault()

    // P2-15: double-submit guard. setSubmitBusy disables the submit
    // button, but a Stimulus action listener still fires for every
    // submit event — a rapid double-click can re-enter handleSubmit
    // before the disabled state has painted, racing two
    // createPaymentMethod calls and two /confirm POSTs against the
    // same booking. The this.submitting flag is set synchronously
    // here so a second handleSubmit on the same task tick returns
    // immediately.
    if (this.submitting) return
    this.submitting = true

    this.clearError()
    this.setSubmitBusy(true)

    if (this.modeValue === "setup_intent") {
      await this.submitSetupIntent()
    } else {
      await this.submitPaymentMethod()
    }
  }

  async submitSetupIntent() {
    const returnUrl = this.hasReturnUrlValue && this.returnUrlValue
      ? this.returnUrlValue
      : window.location.href

    // confirmSetup handles 3DS modals inline. redirect: 'if_required'
    // keeps card-only payments in-page; only redirect-based methods
    // (rare for our use case) would navigate away.
    const { error, setupIntent } = await this.stripe.confirmSetup({
      elements: this.elements,
      confirmParams: { return_url: returnUrl },
      redirect: "if_required"
    })

    if (error) {
      this.showError(error.message)
      this.setSubmitBusy(false)
      return
    }

    // Success → post setup_intent.id to the server. The form posts
    // multipart with CSRF + the hidden setup_intent_id input.
    const form = this.hostForm()
    if (this.hasSetupIntentIdTarget) {
      this.setupIntentIdTarget.value = setupIntent.id
    } else if (form) {
      // Defensive: if no hidden input is present, append one to the form.
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "setup_intent_id"
      input.value = setupIntent.id
      form.appendChild(input)
    }

    this.submitHostForm()
  }

  async submitPaymentMethod() {
    // elements.submit() collects + validates the user's input before
    // we ask Stripe to mint a PaymentMethod. Required for the new
    // PaymentElement API.
    const { error: submitError } = await this.elements.submit()
    if (submitError) {
      this.showError(submitError.message)
      this.setSubmitBusy(false)
      return
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      elements: this.elements
    })

    if (error) {
      this.showError(error.message)
      this.setSubmitBusy(false)
      return
    }

    this.paymentMethodIdTarget.value = paymentMethod.id

    // P2-1 follow-up: explicit Turnstile execute (Cloudflare's
    // recommended pattern for forms). The widget is rendered with
    // data-execution="execute" so the challenge does NOT auto-run on
    // page load. We trigger it here, AFTER the user has clicked submit
    // and Stripe has minted the PaymentMethod, so the token is fresh
    // (no 5-min expiry race) and tied to a clear user-action moment.
    //
    // The previous auto-render pattern was fragile in multiple ways:
    //   - Token can expire if the user spent > 5min on the page
    //   - Auto-execute timing varied across browsers / network conditions
    //   - Silent failure when scripts were delayed
    //
    // Explicit execute eliminates all three.
    const turnstileResult = await this.executeTurnstile()
    if (turnstileResult.error) {
      this.showError(turnstileResult.error)
      this.setSubmitBusy(false)
      return
    }

    this.submitHostForm()
  }

  // Explicit Turnstile execution. Returns `{}` on success (token injected
  // into the form), or `{ error: <user-facing message> }` on failure.
  //
  // No-op success (`{}`) when the form has no Turnstile widget — used by
  // the portal Add-Card flow which doesn't require captcha.
  //
  // Cloudflare's turnstile.execute() requires TWO arguments since the
  // 2024 API revision: the container element AND a params object with
  // sitekey + callbacks. The data-callback / data-error-callback HTML
  // attributes are only read at render time; calling execute() needs the
  // callbacks passed directly in the params object.
  async executeTurnstile() {
    const form = this.hostForm()
    const widget = form?.querySelector(".cf-turnstile")
    if (!widget) return {}

    const turnstile = await this.waitForTurnstileScript()
    if (!turnstile) {
      return {
        error: "Couldn't reach the verification service. If you have an ad-blocker or privacy extension, allow challenges.cloudflare.com for this site, then reload and try again."
      }
    }

    return new Promise((resolve) => {
      let resolved = false
      let timeoutId = null

      const finish = (result) => {
        if (resolved) return
        resolved = true
        if (timeoutId) clearTimeout(timeoutId)
        resolve(result)
      }

      timeoutId = setTimeout(() => {
        // F-HIGH-4: cancel the in-flight challenge so a late-arriving
        // callback can't write a stale token into a form the user has
        // already abandoned. resolved=true guards the JS-side double-
        // finish; turnstile.reset clears Cloudflare's side.
        try {
          window.turnstile?.reset?.(widget)
        } catch (_) {
          // best-effort; the resolve gate still protects against double-finish.
        }
        finish({
          error: "Verification timed out. Reload and try again. If this keeps happening, check that your browser allows challenges.cloudflare.com."
        })
      }, 10000)

      try {
        turnstile.execute(widget, {
          sitekey: widget.getAttribute("data-sitekey"),
          callback: (token) => {
            if (resolved) return
            this.setTurnstileTokenInForm(form, token)
            finish({})
          },
          "error-callback": (errCode) => {
            if (resolved) return
            // Cloudflare error codes documented at
            //   https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/
            // Structured console event so ops dashboards can parse;
            // the user-visible message keeps the code out (per
            // "name it, then solve it" — codes aren't names users can act on).
            console.warn(JSON.stringify({event: "turnstile.error_callback", code: errCode}))
            finish({ error: "Verification failed. Reload and try again." })
          }
        })
      } catch (e) {
        console.warn(JSON.stringify({event: "turnstile.execute_threw", message: e?.message}))
        finish({ error: "Verification failed. Reload and try again." })
      }
    })
  }

  // Wait for Cloudflare's Turnstile script to load (window.turnstile).
  // Returns the turnstile object on success, null on timeout. The script
  // is included in the page's <head> with async + defer, so this gives it
  // a generous window to finish loading even on slow networks.
  async waitForTurnstileScript(timeoutMs = 6000, pollMs = 100) {
    const start = Date.now()
    while (Date.now() - start < timeoutMs) {
      if (window.turnstile && typeof window.turnstile.execute === "function") {
        return window.turnstile
      }
      await new Promise((resolve) => setTimeout(resolve, pollMs))
    }
    return null
  }

  // Find or create the cf-turnstile-response hidden input in the form and
  // set its value to the freshly-executed token. Stripe's form.submit()
  // bypasses the submit event so this hidden field is the only way the
  // server sees the token.
  setTurnstileTokenInForm(form, token) {
    let input = form.querySelector('input[name="cf-turnstile-response"]')
    if (!input) {
      input = document.createElement("input")
      input.type = "hidden"
      input.name = "cf-turnstile-response"
      form.appendChild(input)
    }
    input.value = token
  }

  // Resolve the actual <form>. For the booking flows, the controller is
  // bound to the form itself; for the portal Add-Card flow, the controller
  // is bound to a parent div and the form is a descendant. Find whichever
  // applies.
  hostForm() {
    if (this.element.matches("form")) return this.element
    return this.element.querySelector("form") || this.element.closest("form")
  }

  // Submit the host form, bypassing event listeners so Stimulus doesn't
  // re-enter handleSubmit and create an infinite loop. .submit() is the
  // legacy DOM method that fires no `submit` event; requestSubmit() WOULD
  // dispatch it (and trip the Stimulus action again). Use .submit() here.
  //
  // The optional formAction value override lets a controller-bound form
  // declare its own data-stripe-card-form-action-value to redirect the
  // post elsewhere — used by the portal Add-Card flow which mounts on a
  // generic parent container whose internal form has no action of its own.
  submitHostForm() {
    const form = this.hostForm()
    if (!form) {
      this.showError("Couldn't submit — form not found. Refresh and try again.")
      this.setSubmitBusy(false)
      return
    }
    if (this.hasFormActionValue && this.formActionValue) {
      form.action = this.formActionValue
      form.method = "post"
    }
    form.submit()
  }
}
